import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import Select from "react-select";

import {
  QUERY_CLIENT,
  QUERY_CLIENT_PREFERENCES,
  getClientFeatureQuery,
} from "../../../../config/graphql/query";
import {
  UPDATE_CLIENT_PREFERENCES,
  REGENERATE_WEBHOOK_SECRET,
  TEST_WEBHOOK,
  TOGGLE_CLIENT_FEATURE,
} from "../../../../config/graphql/mutation";

import { useClientId } from "../hooks/useClient";
import useHasRole from "../../../../lib/hooks/useHasRole";
import Input from "../../../../components/Input";
import { reactSelectCustomStyles } from "../../../Employees/Employee/Information";
import { ToggleFeatureButton } from "../Shelly/ShellyControllers";

type FieldValues = {
  preferences: {
    webhook: IWebhookPreferences;
  };
};

type EventRow = {
  path:
    | "preferences.webhook.events.visitor.checkin"
    | "preferences.webhook.events.visitor.checkout"
    | "preferences.webhook.events.meetingAttendee.checkin"
    | "preferences.webhook.events.meetingAttendee.checkout"
    | "preferences.webhook.events.employee.checkin"
    | "preferences.webhook.events.employee.checkout"
    | "preferences.webhook.events.guest.checkin"
    | "preferences.webhook.events.guest.checkout"
    | "preferences.webhook.events.delivery.package"
    | "preferences.webhook.events.delivery.food";
  event: WebhookEventName;
  labelKey: string;
};

const EVENT_ROWS: EventRow[] = [
  {
    path: "preferences.webhook.events.employee.checkin",
    event: "employee_checkin",
    labelKey: "webhook:events.employee.checkin",
  },
  {
    path: "preferences.webhook.events.employee.checkout",
    event: "employee_checkout",
    labelKey: "webhook:events.employee.checkout",
  },
  {
    path: "preferences.webhook.events.meetingAttendee.checkin",
    event: "meetingAttendee_checkin",
    labelKey: "webhook:events.meetingAttendee.checkin",
  },
  {
    path: "preferences.webhook.events.meetingAttendee.checkout",
    event: "meetingAttendee_checkout",
    labelKey: "webhook:events.meetingAttendee.checkout",
  },
  {
    path: "preferences.webhook.events.guest.checkin",
    event: "guest_checkin",
    labelKey: "webhook:events.guest.checkin",
  },
  {
    path: "preferences.webhook.events.guest.checkout",
    event: "guest_checkout",
    labelKey: "webhook:events.guest.checkout",
  },
  {
    path: "preferences.webhook.events.visitor.checkin",
    event: "visitor_checkin",
    labelKey: "webhook:events.visitor.checkin",
  },
  {
    path: "preferences.webhook.events.visitor.checkout",
    event: "visitor_checkout",
    labelKey: "webhook:events.visitor.checkout",
  },
  {
    path: "preferences.webhook.events.delivery.package",
    event: "delivery_package",
    labelKey: "webhook:events.delivery.package",
  },
  {
    path: "preferences.webhook.events.delivery.food",
    event: "delivery_food",
    labelKey: "webhook:events.delivery.food",
  },
];

const SAMPLE_HOST = {
  id: "507f1f77bcf86cd799439011",
  displayName: "Jane Host",
  email: "jane.host@example.com",
  phone: "+31600000000",
  company: "Globex Industries",
};

const SAMPLE_VISITOR = {
  id: "507f1f77bcf86cd799439012",
  displayName: "John Doe",
  email: "john.doe@example.com",
  phone: "+31611111111",
  company: "Acme Corp",
};

const SAMPLE_MEETING = {
  id: "507f1f77bcf86cd799439013",
  subject: "Quarterly review",
  startTime: "2026-04-09T09:00:00.000Z",
  endTime: "2026-04-09T10:00:00.000Z",
};

const buildSamplePayload = (event: WebhookEventName) => {
  const [entity, action] = event.split("_");
  const data: Record<string, unknown> = {
    accessEventId: "507f1f77bcf86cd799439099",
    checkinTime: "2026-04-09T10:23:45.000Z",
    checkoutTime: null,
    checkoutSource: null,
    checkoutReported: false,
  };

  switch (event) {
    case "visitor_checkin":
    case "visitor_checkout": {
      const { company: _company, ...visitorWithoutCompany } = SAMPLE_VISITOR;
      data.visitor = visitorWithoutCompany;
      if (action === "checkout") {
        data.checkoutTime = "2026-04-09T14:50:00.000Z";
      }
      break;
    }
    case "guest_checkin":
    case "guest_checkout":
      data.guest = SAMPLE_VISITOR;
      data.host = SAMPLE_HOST;
      if (action === "checkout") {
        data.checkoutTime = "2026-04-09T14:50:00.000Z";
      }
      break;
    case "meetingAttendee_checkin":
    case "meetingAttendee_checkout":
      data.attendee = SAMPLE_VISITOR;
      data.host = SAMPLE_HOST;
      data.meeting = SAMPLE_MEETING;
      if (action === "checkout") {
        data.checkoutTime = "2026-04-09T14:50:00.000Z";
      }
      break;
    case "employee_checkin":
    case "employee_checkout":
      data.employee = SAMPLE_HOST;
      if (action === "checkout") {
        data.checkoutTime = "2026-04-09T17:00:00.000Z";
      }
      break;
    case "delivery_package":
    case "delivery_food":
      data.recipient = { ...SAMPLE_HOST, isCustom: false };
      break;
    default:
      break;
  }

  return {
    event: `${entity}.${action}`,
    version: "1",
    timestamp: new Date().toISOString(),
    clientId: "000000000000000000000000",
    data,
  };
};

const WebhookSettings = React.memo(() => {
  const { t } = useTranslation(["webhook", "common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);
  const isClientAdmin = useHasRole(["CLIENT_ADMIN"]);
  const canEdit = isAdmin || isClientAdmin;

  const [previewEvent, setPreviewEvent] =
    useState<WebhookEventName>("employee_checkin");
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null);

  const { data } = useQuery<{
    client: { preferences: IClientPreferences };
  }>(QUERY_CLIENT_PREFERENCES, {
    skip: !clientId,
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const webhook = data?.client?.preferences?.webhook;

  const [onUpdate] = useMutation(UPDATE_CLIENT_PREFERENCES, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: [{ query: QUERY_CLIENT, variables: { id: clientId } }],
  });

  const [onRegenerateSecret, { loading: regenerating }] = useMutation<{
    regenerateWebhookSecret: { secret: string; secretCreatedAt: string };
  }>(REGENERATE_WEBHOOK_SECRET, {
    context: { headers: { ClientId: clientId } },
    refetchQueries: [{ query: QUERY_CLIENT, variables: { id: clientId } }],
  });

  const [onTestWebhook, { loading: testing }] = useMutation<{
    testWebhook: {
      delivered: boolean;
      status?: number;
      error?: string;
      responseBody?: string;
    };
  }>(TEST_WEBHOOK, {
    context: { headers: { ClientId: clientId } },
  });

  const methods = useForm<FieldValues>({ shouldFocusError: false });
  const isFormInitializedRef = useRef(false);

  useEffect(() => {
    if (webhook && !isFormInitializedRef.current) {
      isFormInitializedRef.current = true;
      methods.reset({
        preferences: {
          webhook: {
            url: webhook.url || "",
            events: {
              visitor: {
                checkin: !!webhook.events?.visitor?.checkin,
                checkout: !!webhook.events?.visitor?.checkout,
              },
              meetingAttendee: {
                checkin: !!webhook.events?.meetingAttendee?.checkin,
                checkout: !!webhook.events?.meetingAttendee?.checkout,
              },
              employee: {
                checkin: !!webhook.events?.employee?.checkin,
                checkout: !!webhook.events?.employee?.checkout,
              },
              guest: {
                checkin: !!webhook.events?.guest?.checkin,
                checkout: !!webhook.events?.guest?.checkout,
              },
              delivery: {
                package: !!webhook.events?.delivery?.package,
                food: !!webhook.events?.delivery?.food,
              },
            },
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhook]);

  const urlWatch = useWatch({
    control: methods.control,
    name: "preferences.webhook.url",
  });
  const eventsWatch = useWatch({
    control: methods.control,
    name: "preferences.webhook.events",
  });

  const onSubmit = async (variables: FieldValues) => {
    try {
      await onUpdate({
        variables: {
          input: {
            id: clientId,
            preferences: {
              webhook: variables.preferences.webhook,
            },
          },
        },
      });
      toast.success<string>(t("webhook:toast.saved"));
    } catch (e: any) {
      toast.error<string>(e.message || t("webhook:toast.saveError"));
    }
  };

  const handleRegenerate = async () => {
    if (webhook?.secretSet) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(t("webhook:secret.regenerateConfirm"));
      if (!ok) {
        return;
      }
    }
    try {
      const { data: res } = await onRegenerateSecret();
      if (res?.regenerateWebhookSecret?.secret) {
        setRevealedSecret(res.regenerateWebhookSecret.secret);
      }
    } catch (e: any) {
      toast.error<string>(e.message || t("webhook:toast.secretError"));
    }
  };

  const handleTest = async () => {
    try {
      const { data: res } = await onTestWebhook({
        variables: { event: previewEvent },
      });
      const r = res?.testWebhook;
      if (r?.delivered) {
        toast.success<string>(
          t("webhook:toast.testDelivered", { status: r.status }),
        );
      } else {
        toast.error<string>(
          t("webhook:toast.testFailed", {
            status: r?.status ?? "-",
            error: r?.error || "",
          }),
        );
      }
    } catch (e: any) {
      toast.error<string>(e.message || t("webhook:toast.testError"));
    }
  };

  const samplePayload = useMemo(
    () => JSON.stringify(buildSamplePayload(previewEvent), null, 2),
    [previewEvent],
  );

  const previewOptions = useMemo(
    () =>
      EVENT_ROWS.filter((row) => {
        const [, , , entity, action] = row.path.split(".");
        return !!(eventsWatch as any)?.[entity]?.[action];
      }).map((row) => ({ value: row.event, label: t(row.labelKey) })),
    [t, eventsWatch],
  );

  useEffect(() => {
    if (previewOptions.length === 0) {
      return;
    }
    if (!previewOptions.some((o) => o.value === previewEvent)) {
      setPreviewEvent(previewOptions[0].value as WebhookEventName);
    }
  }, [previewOptions, previewEvent]);

  const selectedPreviewOption =
    previewOptions.find((o) => o.value === previewEvent) ?? null;
  const hasCheckedEvents = previewOptions.length > 0;

  return (
    <div className="container-fluid">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="mb-3">
            <h5>{t("webhook:title")}</h5>
            <p className="text-primary mb-0">{t("webhook:subtitle")}</p>
          </div>

          <div className="row">
            <div className="col-xl-7 col-lg-12">
              <div className="mb-3">
                <label htmlFor="webhook-url">{t("webhook:form.url")}</label>
                <Input
                  id="webhook-url"
                  name="preferences.webhook.url"
                  placeholder="https://example.com/webhooks/nameboard"
                  disabled={!canEdit}
                />
                <small className="text-muted">
                  {t("webhook:form.urlHelp")}
                </small>
              </div>

              <div className="mb-4">
                <h6>{t("webhook:secret.title")}</h6>
                <p className="text-primary mb-2">{t("webhook:secret.help")}</p>
                {webhook?.secretSet ? (
                  <p className="mb-2">
                    {t("webhook:secret.createdAt", {
                      date: webhook.secretCreatedAt
                        ? new Date(webhook.secretCreatedAt).toLocaleString()
                        : "-",
                    })}
                  </p>
                ) : (
                  <p className="mb-2 text-danger">{t("webhook:secret.none")}</p>
                )}
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={!canEdit || regenerating}
                  onClick={handleRegenerate}
                >
                  {webhook?.secretSet
                    ? t("webhook:secret.regenerate")
                    : t("webhook:secret.generate")}
                </button>

                {revealedSecret && (
                  <div className="alert alert-primary mt-3">
                    <strong>{t("webhook:secret.revealTitle")}</strong>
                    <p className="mb-2">{t("webhook:secret.revealWarning")}</p>
                    <p
                      className="d-block p-2 bg-light text-dark"
                      style={{ wordBreak: "break-all" }}
                    >
                      {revealedSecret}
                    </p>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary mt-2 color-black"
                      onClick={() => {
                        navigator.clipboard.writeText(revealedSecret);
                        toast.success<string>(t("webhook:secret.copied"));
                      }}
                    >
                      {t("webhook:secret.copy")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary mt-2 ml-2"
                      onClick={() => setRevealedSecret(null)}
                    >
                      {t("webhook:secret.dismiss")}
                    </button>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <h6>{t("webhook:security.title")}</h6>
                <p className="text-primary mb-2">
                  {t("webhook:security.intro")}
                </p>
                <ul className="mb-2 small">
                  <li>
                    <span>X-Nameboard-Event</span> —{" "}
                    {t("webhook:security.headers.event")}
                  </li>
                  <li>
                    <span>X-Nameboard-Timestamp</span> —{" "}
                    {t("webhook:security.headers.timestamp")}
                  </li>
                  <li>
                    <span>X-Nameboard-Signature</span> —{" "}
                    {t("webhook:security.headers.signature")}
                  </li>
                </ul>
                <details>
                  <summary
                    className="small text-primary"
                    style={{ cursor: "pointer" }}
                  >
                    {t("webhook:security.showExample")}
                  </summary>
                  <pre
                    className="p-3 bg-light border rounded mt-2"
                    style={{ fontSize: 12, overflow: "auto" }}
                  >
                    {`const crypto = require("crypto");
// Assumes the webhook route is mounted with:
//   express.raw({ type: "application/json" })
const signatureHeader = req.header("X-Nameboard-Signature") || "";
const timestamp = req.header("X-Nameboard-Timestamp") || "";
const secret = process.env.NAMEBOARD_WEBHOOK_SECRET;
const rawBody = req.body.toString("utf8");

// Reject requests older than 5 minutes (replay protection)
const now = Math.floor(Date.now() / 1000);
if (Math.abs(now - parseInt(timestamp, 10)) > 300) {
  return res.status(401).end();
}

const expected =
  "sha256=" +
  crypto
    .createHmac("sha256", secret)
    .update(\`\${timestamp}.\${rawBody}\`)
    .digest("hex");

const sigBuf = Buffer.from(signatureHeader);
const expBuf = Buffer.from(expected);
if (
  sigBuf.length !== expBuf.length ||
  !crypto.timingSafeEqual(sigBuf, expBuf)
) {
  return res.status(401).end();
}`}
                  </pre>
                </details>
              </div>

              <div className="mb-4">
                <h6>{t("webhook:events.title")}</h6>
                <p className="text-primary mb-2">{t("webhook:events.help")}</p>
                <div className="row">
                  {EVENT_ROWS.map((row) => (
                    <div className="col-6" key={row.path}>
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={row.path}
                          disabled={!canEdit}
                          {...methods.register(row.path)}
                        />
                        <label className="form-check-label" htmlFor={row.path}>
                          {t(row.labelKey)}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary mb-2"
                disabled={!canEdit}
              >
                {t("common:save")}
              </button>
            </div>

            <div className="col-xl-5 col-lg-12">
              <div className="mb-4">
                <h6>{t("webhook:preview.title")}</h6>
                <div className="mb-3">
                  <label htmlFor="webhook-preview-event">
                    {t("webhook:preview.eventLabel")}
                  </label>
                  <div className="d-flex align-items-start">
                    <div style={{ flex: 1 }}>
                      <Select
                        inputId="webhook-preview-event"
                        options={previewOptions}
                        value={selectedPreviewOption}
                        onChange={(selected) =>
                          selected &&
                          setPreviewEvent(selected.value as WebhookEventName)
                        }
                        isSearchable={false}
                        isDisabled={!hasCheckedEvents}
                        placeholder={
                          hasCheckedEvents
                            ? undefined
                            : t("webhook:preview.noEventsSelected")
                        }
                        styles={reactSelectCustomStyles(false)}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary ml-3"
                      disabled={
                        !canEdit || !urlWatch || !hasCheckedEvents || testing
                      }
                      onClick={handleTest}
                    >
                      {t("webhook:preview.test")}
                    </button>
                  </div>
                  <small className="text-muted d-block mt-1">
                    {t("webhook:preview.testHint")}
                  </small>
                </div>
                <pre
                  className="p-3 bg-light border rounded"
                  style={{ maxHeight: 520, overflow: "auto", fontSize: 12 }}
                >
                  {samplePayload}
                </pre>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
});

const WebhookFeature: React.FC = () => {
  const { t } = useTranslation(["common"]);
  const clientId = useClientId();
  const isAdmin = useHasRole(["ADMIN"]);

  const { data } = useQuery(getClientFeatureQuery("webhook"), {
    variables: { id: clientId },
    context: { headers: { ClientId: clientId } },
    fetchPolicy: "network-only",
  });

  const [toggleFeature] = useMutation(TOGGLE_CLIENT_FEATURE, {
    refetchQueries: [
      { query: getClientFeatureQuery("webhook"), variables: { id: clientId } },
    ],
  });

  const webhookEnabled = data?.client?.preferences?.webhook?.enabled ?? false;

  const onToggle = useCallback(
    async (enabled: boolean) => {
      try {
        await toggleFeature({
          variables: {
            input: {
              id: clientId,
              feature: "webhook",
              enabled,
            },
          },
        });
        toast.success<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}`, {
            feature: "Webhook",
          }),
        );
      } catch (e) {
        toast.error<string>(
          t(`common:premiumFeature.${enabled ? "enabled" : "disabled"}Error`, {
            feature: "Webhook",
          }),
        );
      }
    },
    [clientId, toggleFeature, t],
  );

  if (!webhookEnabled) {
    return (
      <div className="container-fluid">
        {isAdmin ? (
          <ToggleFeatureButton
            featureName="Webhook"
            onClick={() => onToggle(true)}
          />
        ) : (
          <div className="text-muted ml-3">
            <h5 className="mb-3">{t("common:premiumFeature.title")}</h5>
            <p>{t("common:premiumFeature.text")}</p>
            <p>{t("common:premiumFeature.contact")}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <WebhookSettings />
      {isAdmin && (
        <div className="container-fluid my-3">
          <ToggleFeatureButton
            featureName="Webhook"
            disable
            onClick={() => onToggle(false)}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(WebhookFeature);
